import React, { useRef, useEffect } from 'react';
import cn from 'bem-cn';

import { Stack, TextArea, HighlightedTextArea, ValidationWrapper, Icon } from '../UI';
import { PromptInfoAuthor } from '../PromptInfo/PromptInfoAuthor/PromptInfoAuthor';
import { CategorySelector } from '../CategorySelector';
import { useUserContext } from '../../contexts';
import { useAiGenerate } from './useAiGenerate';
import { useIconRotation } from './useIconRotation';

import { NewPromptFormProps } from './NewPromptForm.types';

import './NewPromptForm.scss';

const b = cn('new-prompt-form');

export const NewPromptForm = ({
    promptText,
    promptTitle,
    promptProblem,
    selectedCategories,
    validationErrors,
    onPromptTextChange,
    onPromptTitleChange,
    onPromptProblemChange,
    onChangeCategories = () => {},
    disabled = false,
}: NewPromptFormProps) => {
    const { user } = useUserContext();
    const { generate } = useAiGenerate();
    const {
        rotation: titleIconRotation,
        startRotation: startTitleRotation,
        stopRotation: stopTitleRotation,
    } = useIconRotation();
    const {
        rotation: problemIconRotation,
        startRotation: startProblemRotation,
        stopRotation: stopProblemRotation,
    } = useIconRotation();
    const categoriesStackRef = useRef<HTMLDivElement>(null);

    const generateTitle = async () => {
        startTitleRotation();
        try {
            const { title: generatedText } = await generate(promptText, 'title');
            onPromptTitleChange?.(generatedText);
        } catch (error) {
            console.error('Ошибка генерации заголовка:', error);
        } finally {
            stopTitleRotation();
        }
    };

    const generateProblem = async () => {
        startProblemRotation();
        try {
            const { description: generatedText } = await generate(promptText, 'problem');
            onPromptProblemChange?.(generatedText);
        } catch (error) {
            console.error('Ошибка генерации описания проблемы:', error);
        } finally {
            stopProblemRotation();
        }
    };

    useEffect(() => {
        if (disabled) {
            return;
        }

        if (!promptTitle) {
            generateTitle();
        }

        if (!promptProblem) {
            generateProblem();
        }

        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handlePromptTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onPromptTextChange?.(e.target.value);
    };

    const handlePromptTitleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onPromptTitleChange?.(e.target.value);
    };

    const handlePromptProblemChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onPromptProblemChange?.(e.target.value);
    };

    return (
        <Stack spacing={32}>
            <Stack spacing={16}>
                <Stack spacing={4}>
                    <h2 className={b('title')}>Промпт</h2>
                    {!disabled && (
                        <p className={b('subtitle')}>
                            Выделите скобками <span>[...]</span> слова, вместо которых нужно вставить свои данные
                        </p>
                    )}
                </Stack>
                <HighlightedTextArea
                    placeholder="Напишите свой промпт"
                    value={promptText}
                    onChange={handlePromptTextChange}
                    error={validationErrors?.prompt}
                    disabled={disabled}
                />
            </Stack>
            <Stack spacing={12} ref={categoriesStackRef}>
                <h2 className={b('title')}>Категории</h2>
                <ValidationWrapper error={validationErrors?.categories}>
                    <div className={b('categories', { error: Boolean(validationErrors?.categories) })}>
                        <CategorySelector
                            selectedCategories={selectedCategories}
                            onChangeCategories={onChangeCategories}
                            scrollToElementOnMenuOpen={categoriesStackRef}
                            maxSelectedCategoriesCount={3}
                            disabled={disabled}
                        />
                    </div>
                </ValidationWrapper>
            </Stack>
            <Stack spacing={12}>
                <Stack spacing={8} direction="row" alignItems="center">
                    <h2 className={b('title')}>Название промпта</h2>
                    {!disabled && <Icon name="ai" onClick={generateTitle} rotate={titleIconRotation} />}
                </Stack>
                <TextArea
                    size="small"
                    value={promptTitle}
                    placeholder="Например: Создание презентаций за 10 минут"
                    onChange={handlePromptTitleChange}
                    error={validationErrors?.title}
                    autoResize
                    disabled={disabled}
                />
            </Stack>
            <Stack spacing={12}>
                <Stack spacing={8} direction="row" alignItems="center">
                    <h2 className={b('title')}>Какую задачу решает промпт</h2>
                    {!disabled && <Icon name="ai" onClick={generateProblem} rotate={problemIconRotation} />}
                </Stack>
                <TextArea
                    size="small"
                    value={promptProblem}
                    placeholder="Например: Создает красивые презентации"
                    onChange={handlePromptProblemChange}
                    error={validationErrors?.problem}
                    autoResize
                    disabled={disabled}
                />
            </Stack>
            <Stack spacing={12} className={b('disabled')}>
                <h2 className={b('title')}>Контакты для связи</h2>
                <PromptInfoAuthor
                    author={{
                        login: user?.login || '',
                        name:
                            user?.firstName && user?.lastName
                                ? `${user?.firstName} ${user?.lastName?.charAt(0)}.`
                                : user?.firstName || user?.login || '',
                        image: user?.avatarSrc || '',
                    }}
                    subtitle={`${user?.login || ''}@yandex.ru`}
                    disabled={disabled}
                />
            </Stack>
            {!disabled && (
                <p className={b('rules-warning')}>
                    Нажимая кнопку «Опубликовать», я соглашаюсь с условиями участия в конкурсе{' '}
                    <a
                        href={`https://${window.location.hostname}/prompthub/new/rules`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        «Промптхаб: лучшее»{' '}
                    </a>
                    и принимаю{' '}
                    <a href="https://yandex.ru/legal/prompthub/" target="_blank" rel="noopener noreferrer">
                        условия использования сервиса
                    </a>
                </p>
            )}
        </Stack>
    );
};
